import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';
import * as ScoringActions from '../actions/scoring.actions';
import * as Reducers from '../reducers';
import {catchError, exhaustMap, filter, map, mergeMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {of} from 'rxjs';
import {ScoringService} from '../../../scoring/services/scoring.service';
import {UnitsFacade} from '../../units/facades/units.facade';
import {RankTypeEnum} from '../../../scoring/models/rank-type.enum';

@Injectable()
export class ScoringEffects {

  // Groups rank
  getGroupsRankData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getRankingData.type),
      map((action: any) => action.rank),
      filter(rank => rank === RankTypeEnum.GROUPS),
      tap(() => this._loader.start()),
      mergeMap((rank) => of(rank).pipe(
        switchMap(() => this._unitsFacade.getUnitGroups$.pipe(filter(x => !!x && x.length > 0))),
        withLatestFrom(this.store$.select(Reducers.getDates)),
        map(data => [rank, ...data])
      )),
      mergeMap(([rank, unitsGroups, dates]) => this._scoringService.getRankingData(rank, dates)
        .pipe(
          map(data => {
            data.forEach(v => v.name = unitsGroups?.find(ug => ug.unitGroupId === v.id)?.name);
            return data;
          }),
          exhaustMap((data) => of(ScoringActions.getRankingDataSuccess({data: data, rank: rank}))),
          catchError(error => of(ScoringActions.getRankingDataFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  // Vehicles rank
  getVehiclesRankData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getRankingData.type),
      map((action: any) => action.rank),
      filter(rank => rank === RankTypeEnum.VEHICLES),
      tap(() => this._loader.start()),
      mergeMap((rank) => of(rank).pipe(
        switchMap(() => this._unitsFacade.getUnits$.pipe(filter(x => !!x && x.length > 0))),
        withLatestFrom(this.store$.select(Reducers.getDates)),
        map(data => [rank, ...data])
      )),
      mergeMap(([rank, units, dates]) => this._scoringService.getRankingData(rank, dates)
        .pipe(
          map(data => {
            data.forEach(v => v.name = units?.find(u => u.unitId === v.id)?.unitName);
            return data;
          }),
          exhaustMap((data) => of(ScoringActions.getRankingDataSuccess({data: data, rank: rank}))),
          catchError(error => of(ScoringActions.getRankingDataFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  // Drivers rank
  getDriversRankData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getRankingData.type),
      map((action: any) => action.rank),
      filter(rank => rank === RankTypeEnum.DRIVERS),
      tap(() => this._loader.start()),
      mergeMap((rank) => of(rank).pipe(
        // switchMap(() => this._unitsFacade.getUnits$.pipe(filter(x => !!x && x.length > 0))),
        switchMap(() => of([])),
        withLatestFrom(this.store$.select(Reducers.getDates)),
        map(data => [rank, ...data])
      )),
      mergeMap(([rank, drivers, dates]) => this._scoringService.getRankingData(rank, dates)
        .pipe(
          map(data => {
            data.forEach((v, index) => v.name = `demo #${index+1}`);
            return data;
          }),
          exhaustMap((data) => of(ScoringActions.getRankingDataSuccess({data: data, rank: rank}))),
          catchError(error => of(ScoringActions.getRankingDataFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  getVehiclesSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getVehiclesSummary.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates)
      ),
      switchMap(([action, dates]) => this._scoringService.getVehiclesSummary(dates[0], dates[1])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getVehiclesSummarySuccess({data: data}))),
          catchError(error => of(ScoringActions.getVehiclesSummaryFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  getVehiclesPeriodStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getVehiclesPeriodStats.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates)
      ),
      switchMap(([action, dates]) => this._scoringService.getVehiclesPeriodStats(dates[0], dates[1])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getVehiclesPeriodStatsSuccess({data: data}))),
          catchError(error => of(ScoringActions.getVehiclesPeriodStatsFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  getGroupsSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getGroupsSummary.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates)
      ),
      switchMap(([action, dates]) => this._scoringService.getGroupsSummary(dates[0], dates[1])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getGroupsSummarySuccess({data: data}))),
          catchError(error => of(ScoringActions.getGroupsSummaryFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  getGroupsPeriodStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getGroupsPeriodStats.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates)
      ),
      switchMap(([action, dates]) => this._scoringService.getGroupsPeriodStats(dates[0], dates[1])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getGroupsPeriodStatsSuccess({data: data}))),
          catchError(error => of(ScoringActions.getGroupsPeriodStatsFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  getDriverSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getDriverSummary.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates)
      ),
      switchMap(([action, dates]) => this._scoringService.getDriverSummary(dates[0], dates[1])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getDriverSummarySuccess({data: data}))),
          catchError(error => of(ScoringActions.getDriverSummaryFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  getDriverPeriodStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getDriverPeriodStats.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates)
      ),
      switchMap(([action, dates]) => this._scoringService.getDriverPeriodStats(dates[0], dates[1])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getDriverPeriodStatsSuccess({data: data}))),
          catchError(error => of(ScoringActions.getDriverPeriodStatsFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );



  getSelectedDriverSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getSelectedDriverSummary.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates),
        this.store$.select(Reducers.getDriver),
      ),
      switchMap(([action, dates, driver]) => this._scoringService.getDriverSummaryById(dates[0], dates[1], driver?.driverId)
        .pipe(
          exhaustMap((data) => of(ScoringActions.getSelectedDriverSummarySuccess({data: data}))),
          catchError(error => of(ScoringActions.getSelectedDriverSummaryFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  getSelectedDriverPeriodStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.getSelectedDriverPeriodStats.type),
      tap(() => this._loader.start()),
      withLatestFrom(
        this.store$.select(Reducers.getDates),
        this.store$.select(Reducers.getDriver),
      ),
      switchMap(([action, dates, driver]) => this._scoringService.getDriverPeriodStatsById(dates[0], dates[1], driver?.driverId, action['periodType'])
        .pipe(
          exhaustMap((data) => of(ScoringActions.getSelectedDriverPeriodStatsSuccess({data: data}))),
          catchError(error => of(ScoringActions.getSelectedDriverPeriodStatsFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  setDriver$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.setDriver.type),
      withLatestFrom(
        this.store$.select(Reducers.getDriverList)
      ),
      switchMap(([action, driverList]) => {
        const driver = driverList?.find(d => d.driverId === action['id']);
        return of(driver)
            .pipe(
              exhaustMap((data) => of(ScoringActions.setDriverSuccess({data: data}))),
              catchError(error => of(ScoringActions.setDriverFailure({error}))),
            );
        }
      )
    )
  );


  changeDateRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScoringActions.changeDateRange.type),
      tap((action: any) => {

        console.log('changeDateRange', action);
      })
    ),
    { dispatch: false }
  );

  private _loader: LoadingBarState;

  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private _loadingBar: LoadingBarService,
    private _scoringService: ScoringService,
    private _unitsFacade: UnitsFacade,
  ) {
    this._loader = this._loadingBar.useRef('main');
  }
}
